import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { FormSpy, useField, useForm } from "react-final-form";
import Collapse from "@mui/material/Collapse";
import { TextInput } from "@vk-hr-tek/ui/input";
import { Autorecognition } from "./Autorecognition.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function TextInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else TextInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
function _object_without_properties(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys, key, i;
    if ("u" > typeof Reflect && Reflect.ownKeys) {
        sourceKeys = Reflect.ownKeys(Object(source));
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
        return target;
    }
    target = _object_without_properties_loose(source, excluded);
    if (Object.getOwnPropertySymbols) {
        sourceKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (!(excluded.indexOf(key) >= 0)) {
                if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
            }
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (null == source) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (!(excluded.indexOf(key) >= 0)) {
            if (Object.prototype.propertyIsEnumerable.call(source, key)) target[key] = source[key];
        }
    }
    return target;
}
const TextInput_TextInput = (_0)=>{
    let { label, labelIcon, name, onChange, type, mask, placeholder, multiline = false, rows = 1, maxRows, showError = true, required = false, tooltip, after, disabled = false, loading = false, clearable = false, recognizedValue, isRecognitionUsedBefore = false, validationWithoutTouch = false, validationWhenDisabled = false, inputMode, inputFilter, autocomplete, testId, validateArray, warnings, maxLength, minLength, helperText, debounceTimeout } = _0, rest = _object_without_properties(_0, [
        "label",
        "labelIcon",
        "name",
        "onChange",
        "type",
        "mask",
        "placeholder",
        "multiline",
        "rows",
        "maxRows",
        "showError",
        "required",
        "tooltip",
        "after",
        "disabled",
        "loading",
        "clearable",
        "recognizedValue",
        "isRecognitionUsedBefore",
        "validationWithoutTouch",
        "validationWhenDisabled",
        "inputMode",
        "inputFilter",
        "autocomplete",
        "testId",
        "validateArray",
        "warnings",
        "maxLength",
        "minLength",
        "helperText",
        "debounceTimeout"
    ]);
    var _meta_data, _meta_data1;
    const { mutators } = useForm();
    const composeValidators = (validators)=>(value, allValues)=>{
            const result = validators.reduce((error, validator)=>error || (null == validator ? void 0 : validator(value, allValues)), void 0);
            return result;
        };
    const composedWarnings = composeValidators(warnings || []);
    const { input, meta } = useField(name, _object_spread({
        validate: composeValidators(validateArray || [])
    }, rest));
    const { onBlur, onChange: onInputChange } = input;
    const [value_0, setValue] = useState(input.value);
    const lastSetByUser = useRef(input.value);
    if (debounceTimeout && input.value !== lastSetByUser.current) {
        setValue(input.value);
        lastSetByUser.current = input.value;
    }
    const handleChangeForm = useCallback((formValue)=>{
        lastSetByUser.current = formValue;
        onInputChange(formValue);
        if (onChange) onChange(formValue);
    }, [
        onInputChange,
        onChange
    ]);
    const handleChangeDebounce = useMemo(()=>debounce(handleChangeForm, debounceTimeout), [
        debounceTimeout,
        handleChangeForm
    ]);
    const handleChange = useCallback((changedValue)=>{
        setValue(changedValue);
        if (debounceTimeout) {
            handleChangeDebounce(changedValue);
            if ('' === changedValue) handleChangeForm(changedValue);
        } else handleChangeForm(changedValue);
    }, [
        debounceTimeout,
        handleChangeDebounce,
        handleChangeForm
    ]);
    const handleBlur = useCallback((event)=>{
        handleChangeDebounce.flush();
        onBlur(event);
    }, [
        handleChangeDebounce,
        onBlur
    ]);
    useEffect(()=>()=>{
            handleChangeDebounce.flush();
        }, [
        handleChangeDebounce
    ]);
    const errorDisabled = disabled || !validationWhenDisabled;
    const finalValue = debounceTimeout ? value_0 : input.value;
    const renderError = useMemo(()=>!errorDisabled && validationWithoutTouch ? !!input.value && !!meta.error : meta.touched && !!meta.error, [
        errorDisabled,
        input.value,
        meta.error,
        meta.touched,
        validationWithoutTouch
    ]);
    const renderWarning = useMemo(()=>{
        var _meta_data, _meta_data1;
        return !errorDisabled && validationWithoutTouch ? !!input.value && !!(null == (_meta_data = meta.data) ? void 0 : _meta_data.warning) : meta.touched && !!(null == (_meta_data1 = meta.data) ? void 0 : _meta_data1.warning);
    }, [
        errorDisabled,
        input.value,
        null == (_meta_data = meta.data) ? void 0 : _meta_data.warning,
        meta.touched,
        validationWithoutTouch
    ]);
    return /*#__PURE__*/ jsx("div", {
        children: /*#__PURE__*/ jsxs(Autorecognition, {
            recognizedValue: recognizedValue,
            isRecognitionUsedBefore: isRecognitionUsedBefore,
            onBlur: handleBlur,
            disabled: disabled,
            setValue: handleChange,
            value: finalValue,
            mask: mask,
            children: [
                /*#__PURE__*/ jsx(TextInput, _object_spread_props(_object_spread({}, input), {
                    testId: testId,
                    label: label,
                    labelIcon: labelIcon,
                    required: required,
                    disabled: disabled,
                    value: finalValue,
                    inputFilter: inputFilter,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    type: type,
                    error: renderError,
                    helperText: renderError && showError && meta.error || helperText,
                    warning: renderWarning && (null == (_meta_data1 = meta.data) ? void 0 : _meta_data1.warning),
                    placeholder: placeholder,
                    tooltip: tooltip,
                    multiline: multiline,
                    rows: rows,
                    maxRows: maxRows,
                    mask: mask,
                    loading: loading,
                    clearable: clearable,
                    inputMode: inputMode,
                    autocomplete: autocomplete,
                    maxLength: maxLength,
                    minLength: minLength
                })),
                /*#__PURE__*/ jsx(FormSpy, {
                    subscription: {
                        values: true
                    },
                    onChange: ({ values })=>{
                        if (values) mutators.setFieldData(input.name, {
                            warning: composedWarnings(values[input.name], values)
                        });
                    }
                }),
                after && 'function' == typeof after && /*#__PURE__*/ jsx(Collapse, {
                    in: meta.visited,
                    children: after(input.value)
                })
            ]
        })
    });
};
const form_TextInput_TextInput = TextInput_TextInput;
export default form_TextInput_TextInput;
